import { useState } from 'react'
import { useNavigate } from 'react-router'
import { 
    Heart, Users, CheckCircle, AlertTriangle, 
    Download, TrendingUp, Home
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { 
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { useHealthStatistics } from '@/hooks/use-health'

export default function HealthStatisticsPage() {
    const navigate = useNavigate()
    
    // Fetch health statistics from backend
    const { data: healthData, isLoading: isHealthLoading, error } = useHealthStatistics()

    // Process data for charts
    const chartData = healthData?.data?.dataPerBulan?.filter(item => item.totalTerdaftar > 0) || []
    
    // Health statistics overview from API data
    const healthStats = healthData?.data ? [
        {
            title: 'Total Terdaftar',
            value: healthData.data.totalTerdaftar.toString(),
            change: `${healthData.data.tahun}`,
            trend: 'neutral' as const,
            icon: Users,
            color: 'blue'
        },
        {
            title: 'Gizi Normal',
            value: healthData.data.totalGiziNormal.toString(),
            change: `${healthData.data.persentaseGiziNormal.toFixed(1)}%`,
            trend: 'up' as const,
            icon: CheckCircle,
            color: 'green'
        },
        {
            title: 'Stunting',
            value: healthData.data.totalStunting.toString(),
            change: `${healthData.data.persentaseStunting.toFixed(1)}%`,
            trend: 'down' as const,
            icon: AlertTriangle,
            color: 'red'
        },
        {
            title: 'Gizi Buruk',
            value: healthData.data.totalGiziBuruk.toString(),
            change: `${healthData.data.persentaseGiziBuruk.toFixed(1)}%`,
            trend: 'down' as const,
            icon: AlertTriangle,
            color: 'orange'
        }
    ] : []

    // Data for pie chart
    const pieData = healthData?.data ? [
        { name: 'Gizi Normal', value: healthData.data.totalGiziNormal, color: '#10b981' },
        { name: 'Stunting', value: healthData.data.totalStunting, color: '#ef4444' },
        { name: 'Gizi Buruk', value: healthData.data.totalGiziBuruk, color: '#f97316' }
    ] : []

    // Data for monthly trend chart
    const monthlyTrendData = chartData.map(item => ({
        bulan: item.namaBulan.split(' ')[0], // Get month name only
        totalTerdaftar: item.totalTerdaftar,
        giziNormal: item.totalGiziNormal,
        stunting: item.totalStunting,
        giziBuruk: item.totalGiziBuruk,
        persentaseGiziNormal: item.persentaseGiziNormal
    }))

    // Error handling
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">Terjadi Kesalahan</h2>
                    <p className="text-gray-600 mb-4">Tidak dapat memuat data statistik kesehatan</p>
                    <Button onClick={() => navigate('/')} variant="outline">
                        Kembali ke Beranda
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/30 to-pink-50/40">
            {/* Enhanced Header Section */}
            <div className="relative bg-gradient-to-r from-red-600 via-pink-700 to-rose-800 overflow-hidden">
                <div className="absolute inset-0 bg-black/10">
                    <div className="absolute inset-0 bg-white/5"></div>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white/5 rounded-full"></div>
                <div className="absolute bottom-0 left-0 -mb-32 -ml-32 w-64 h-64 bg-white/5 rounded-full"></div>
                
                <div className="relative container mx-auto px-6 py-16">
                    <div className="max-w-4xl">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm border border-white/20">
                                <Heart className="h-8 w-8 text-white" />
                            </div>
                            <div className="flex items-center space-x-2 text-white/80 text-sm">
                                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                                <span>Dashboard</span>
                                <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                                <span>Statistik Kesehatan</span>
                            </div>
                        </div>
                        
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                            Statistik Kesehatan {healthData?.data?.tahun || '2024'}
                        </h1>
                        
                        <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-3xl">
                            Data status gizi dan kesehatan masyarakat Desa Tatadesa berdasarkan pencatatan real-time
                        </p>

                        {/* Action Button */}
                        <div className="flex flex-wrap gap-4">
                            <Button 
                                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                                onClick={() => navigate('/')}
                            >
                                <Home className="h-5 w-5 mr-2" />
                                Kembali ke Beranda
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-12 space-y-12">
                {/* Health Stats Overview */}
                <section>
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-red-800 to-gray-900 bg-clip-text text-transparent mb-4">
                            Ringkasan Status Gizi
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Data status gizi masyarakat berdasarkan pemantauan tahun {healthData?.data?.tahun || '2024'}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {isHealthLoading ? (
                            // Loading skeleton
                            [...Array(4)].map((_, i) => (
                                <Card key={i} className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg rounded-2xl overflow-hidden">
                                    <CardContent className="p-6">
                                        <div className="animate-pulse">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="w-12 h-12 bg-gray-200 rounded-2xl"></div>
                                                <div className="w-16 h-4 bg-gray-200 rounded"></div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="w-24 h-3 bg-gray-200 rounded"></div>
                                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        ) : error ? (
                            // Error state
                            <div className="col-span-full">
                                <Card className="bg-red-50 border-red-200">
                                    <CardContent className="p-6 text-center">
                                        <p className="text-red-600">Error loading health statistics</p>
                                    </CardContent>
                                </Card>
                            </div>
                        ) : (
                            // Actual data
                            healthStats.map((stat, index) => (
                                <Card key={stat.title} className="group bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 rounded-2xl overflow-hidden">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className={`p-3 rounded-2xl transition-all duration-300 group-hover:scale-110 ${
                                                stat.color === 'red' ? 'bg-gradient-to-br from-red-500 to-red-600' :
                                                stat.color === 'green' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                                                stat.color === 'blue' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                                'bg-gradient-to-br from-orange-500 to-orange-600'
                                            } shadow-lg`}>
                                                <stat.icon className="h-6 w-6 text-white" />
                                            </div>
                                            <div className={`flex items-center text-sm font-medium ${
                                                stat.trend === 'up' ? 'text-green-600' : 
                                                stat.trend === 'down' ? 'text-red-600' : 
                                                'text-gray-600'
                                            }`}>
                                                {stat.trend === 'up' && <TrendingUp className="h-4 w-4 mr-1" />}
                                                {stat.change}
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                                                {stat.title}
                                            </h3>
                                            <p className="text-3xl font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                                                {stat.value}
                                            </p>
                                        </div>
                                        
                                        {/* Decorative bottom line */}
                                        <div className={`mt-4 h-1 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left ${
                                            stat.color === 'red' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                            stat.color === 'green' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                            stat.color === 'blue' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                            'bg-gradient-to-r from-orange-500 to-orange-600'
                                        }`}></div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>
                </section>

                {/* Charts Section */}
                {healthData?.data && chartData.length > 0 && (
                    <>
                        {/* Pie Chart for Nutrition Status */}
                        <section>
                            <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg rounded-2xl">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                                        Distribusi Status Gizi
                                    </h3>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value) => [value, 'Jumlah']} />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </section>

                        {/* Monthly Trend Chart */}
                        <section>
                            <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg rounded-2xl">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                                        Tren Bulanan Status Gizi
                                    </h3>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={monthlyTrendData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="bulan" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="giziNormal" fill="#10b981" name="Gizi Normal" />
                                                <Bar dataKey="stunting" fill="#ef4444" name="Stunting" />
                                                <Bar dataKey="giziBuruk" fill="#f97316" name="Gizi Buruk" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </section>

                        {/* Percentage Trend Line Chart */}
                        <section>
                            <Card className="bg-white/90 backdrop-blur-sm border border-gray-200 shadow-lg rounded-2xl">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                                        Persentase Gizi Normal Per Bulan
                                    </h3>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={monthlyTrendData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="bulan" />
                                                <YAxis domain={[0, 100]} />
                                                <Tooltip formatter={(value) => [`${value}%`, 'Persentase']} />
                                                <Legend />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="persentaseGiziNormal" 
                                                    stroke="#10b981" 
                                                    strokeWidth={3}
                                                    name="Gizi Normal (%)"
                                                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </section>
                    </>
                )}

                {/* Information Card */}
                <section>
                    <Card className="bg-gradient-to-r from-red-50 via-pink-50 to-rose-50 border border-red-200 shadow-lg rounded-2xl">
                        <CardContent className="p-8">
                            <div className="text-center">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                    Statistik Kesehatan Desa
                                </h3>
                                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                                    Data real-time status gizi dan kesehatan masyarakat berdasarkan pencatatan posyandu dan program kesehatan desa
                                </p>
                                <div className="flex flex-wrap gap-4 justify-center">
                                    <Button 
                                        className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl px-6 py-3 shadow-lg font-semibold"
                                        onClick={() => navigate('/')}
                                    >
                                        <Heart className="h-5 w-5 mr-2" />
                                        Kembali ke Beranda
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            </div>
        </div>
    )
}
